import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Copy, Loader2, MoreHorizontal, Plus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SettingsMetaPage } from "./SettingsMetaPageLayout";
import { useBreadcrumb } from "@/contexts/BreadcrumbContext";

interface MetaCustomAudience {
  id: string;
  name?: string;
}

export function SettingsMetaBusinessAdAccountAudiencesRoute() {
  const { businessId, adAccountId } = useParams<{ businessId: string; adAccountId: string }>();
  const { setBreadcrumbs, clearBreadcrumbs } = useBreadcrumb();
  const [isLoading, setIsLoading] = useState(true);
  const [audiences, setAudiences] = useState<MetaCustomAudience[]>([]);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [audienceName, setAudienceName] = useState("");
  const [isCreating, setIsCreating] = useState(false);

  const fetchAudiences = useCallback(async () => {
    if (!businessId || !adAccountId) return;

    setIsLoading(true);
    try {
      const [businessResponse, adAccountResponse, audiencesResponse] = await Promise.all([
        fetch(`/api/meta/businesses/${businessId}`),
        fetch(`/api/meta/ad-accounts/${adAccountId}`),
        fetch(`/api/meta/ad-accounts/${adAccountId}/customaudiences?fields=name`),
      ]);

      const businessName =
        businessResponse.ok
          ? ((await businessResponse.json()) as { business?: { name?: string } }).business
              ?.name || businessId
          : businessId;

      const adAccountName =
        adAccountResponse.ok
          ? ((await adAccountResponse.json()) as { adAccount?: { name?: string } }).adAccount
              ?.name || adAccountId
          : adAccountId;

      setBreadcrumbs([
        { label: "Settings", path: "/settings" },
        { label: "Integrations", path: "/settings/integrations" },
        { label: "Meta", path: "/settings/integrations/meta" },
        { label: businessName, path: `/settings/meta/businesses/${businessId}` },
        {
          label: "Ad Accounts",
          path: `/settings/meta/businesses/${businessId}/ad-accounts`,
        },
        {
          label: adAccountName,
          path: `/settings/meta/businesses/${businessId}/ad-accounts/${adAccountId}`,
        },
        {
          label: "Audiences",
          path: `/settings/meta/businesses/${businessId}/ad-accounts/${adAccountId}/audiences`,
        },
      ]);

      if (audiencesResponse.ok) {
        const data = (await audiencesResponse.json()) as { audiences?: MetaCustomAudience[] };
        setAudiences(data.audiences ?? []);
      } else {
        console.error("Failed to fetch Meta custom audiences");
      }
    } catch (error) {
      console.error("Failed to fetch Meta custom audiences:", error);
    } finally {
      setIsLoading(false);
    }
  }, [businessId, adAccountId, setBreadcrumbs]);

  useEffect(() => {
    fetchAudiences();

    return () => {
      clearBreadcrumbs();
    };
  }, [fetchAudiences, clearBreadcrumbs]);

  const handleOpenCreateDialog = () => {
    setAudienceName("");
    setIsCreateDialogOpen(true);
  };

  const handleCreate = async () => {
    if (!adAccountId || !audienceName.trim()) {
      toast.error("Audience name is required");
      return;
    }

    setIsCreating(true);
    try {
      const response = await fetch(`/api/meta/ad-accounts/${adAccountId}/customaudiences`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: audienceName.trim() }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to create audience");
      }

      const data = (await response.json()) as { audience: MetaCustomAudience };
      setAudiences((current) => [data.audience, ...current]);
      setIsCreateDialogOpen(false);
      setAudienceName("");
      toast.success("Audience created");
    } catch (error) {
      console.error("Failed to create audience:", error);
      toast.error(error instanceof Error ? error.message : "Failed to create audience");
    } finally {
      setIsCreating(false);
    }
  };

  const handleCopyId = async ({ id }: { id: string }) => {
    try {
      await navigator.clipboard.writeText(id);
      toast.success("ID copied to clipboard");
    } catch (error) {
      console.error("Failed to copy audience ID:", error);
      toast.error("Failed to copy ID");
    }
  };

  if (isLoading) {
    return (
      <SettingsMetaPage className="flex justify-center items-center min-h-[40vh]">
        <Loader2 className="animate-spin h-12 w-12 text-muted-foreground" />
      </SettingsMetaPage>
    );
  }

  return (
    <SettingsMetaPage>
      <div className="flex items-center justify-between gap-4 mb-4">
        <h3 className="text-lg font-semibold">Audiences</h3>
        <Button onClick={handleOpenCreateDialog}>
          <Plus className="h-4 w-4 mr-2" />
          Create
        </Button>
      </div>

      {audiences.length === 0 ? (
        <p className="text-sm text-muted-foreground">No audiences found.</p>
      ) : (
        audiences.map((audience) => (
          <div
            key={audience.id}
            className="flex items-center justify-between border rounded-lg p-4"
          >
            <span className="font-medium">{audience.name || audience.id}</span>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 shrink-0"
                  aria-label="Audience actions"
                >
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => handleCopyId({ id: audience.id })}>
                  <Copy className="h-4 w-4 mr-2" />
                  Copy ID
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        ))
      )}

      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Audience</DialogTitle>
            <DialogDescription>
              Create a new custom audience for this ad account.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="audience-name">Name</Label>
              <Input
                id="audience-name"
                value={audienceName}
                onChange={(event) => setAudienceName(event.target.value)}
                placeholder="e.g. Newsletter subscribers"
                onKeyDown={(event) => {
                  if (event.key === "Enter" && !isCreating) {
                    handleCreate();
                  }
                }}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsCreateDialogOpen(false)}
              disabled={isCreating}
            >
              Cancel
            </Button>
            <Button onClick={handleCreate} disabled={isCreating || !audienceName.trim()}>
              {isCreating ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : (
                "Create"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </SettingsMetaPage>
  );
}
